import { useLocation } from "react-router-dom";
import {
  ACCESS_SCANS_REPORT,
  ACCESS_SCANS_REPORT_COUNT,
  ACCESS_SCANS_REPORT_OPTIMIZED
} from "./fragment";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { userStore } from "../../../../store/user";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { AccessScansReportRow, DisplayType } from "./type";
import { useEffect, useRef, useState, useMemo } from "react";
import { eventStore } from "../../../../store/event";
import { GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { SecondaryButton, ToggleButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { formatData, generateGroupedColDefs, getUniqueTokenTypes, getUniqueLocations, formatHourlyIngressEgressData, formatHourlyDataForTable, generateHourlyColDefs, HourlyData, formatHourlyAttendanceData, HourlyAttendanceData, formatHourlyAttendanceDataForTable } from "./helpers";
import { remapAccessTokenTypes } from "./tokenNameRemap";
import Loader from "../../../../components/Loader";
import CustomSelect from "../../../../components/customSelect";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis, Line, LineChart, Legend } from "recharts";
import moment from "moment";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
const PAGE_SIZE = 50000;
const DEBOUNCE_DELAY = 100;

const displayTypeOptions = [
  { value: 'all_scans', label: 'All Scans' },
  { value: 'by_location', label: 'By Location' },
  { value: 'by_token_type', label: 'By Access Type' },
  { value: 'by_date', label: 'By Date' },
  { value: 'by_location_token_type', label: 'By Location → Access Type' },
  { value: 'by_date_location', label: 'By Date → By Location' },
  { value: 'by_hour_ingress_egress', label: 'Hourly Ingress/Egress' },
  { value: 'by_hour_attendance', label: 'Hourly Attendance' },
];

const BarChartComponent = ({
  rows,
  selectedDataKey,
  displayType,
}: {
  rows: AccessScansReportRow[];
  selectedDataKey: string;
  displayType: DisplayType;
}) => {
  // Handle empty data
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-4">
        <div className="text-gray-500 text-sm">No data to display</div>
      </div>
    );
  }

  // Create chart labels based on display type
  const dataWithLabels = rows.map(row => {
    let label = '';
    if (displayType === 'by_location') {
      label = row.location_name || 'Unknown';
    } else if (displayType === 'by_token_type') {
      label = row.access_token_type || 'Unknown';
    } else if (displayType === 'by_date' || displayType === 'by_date_location') {
      const date = row.group_date || '';
      label = date !== 'Total' ? moment(date).format('MMM DD, YYYY') : date;
    } else if (displayType === 'by_location_token_type') {
      label = row.location_name || 'Unknown';
    } else {
      label = row.location_name || row.access_token_type || 'Unknown';
    }
    return { ...row, chartLabel: label };
  });

  // Filter out total rows
  const filteredData = dataWithLabels.filter(item => !item.isTotalRow);

  // Sort by selected metric
  const sortedData = filteredData.sort((a, b) => {
    const aVal = (a as any)[selectedDataKey] as number || 0;
    const bVal = (b as any)[selectedDataKey] as number || 0;
    return bVal - aVal;
  });

  // Limit to top 20
  const limitedData = sortedData.slice(0, 20);

  // Formatter for success rate (percentage)
  const percentFormatter = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Formatter for counts
  const countFormatter = (value: number) => {
    return value.toString();
  };

  const isPercentage = selectedDataKey === 'success_rate';
  const formatter = isPercentage ? percentFormatter : countFormatter;

  // Dynamic height calculation for labels
  const maxLabelLength = Math.max(
    ...limitedData.map((item) => (item.chartLabel ? item.chartLabel.length : 0))
  );
  const angleInRadians = (Math.abs(-30) * Math.PI) / 180;
  const textHeight = Math.sin(angleInRadians) * maxLabelLength * 10;
  const xAxisHeight = textHeight > 50 ? textHeight : 50;

  // Determine bar color based on metric
  let barColor = '#B71C1C'; // Default red
  if (selectedDataKey === 'success_count') {
    barColor = '#059669'; // Green
  } else if (selectedDataKey === 'failed_count') {
    barColor = '#dc2626'; // Red
  }

  // Format data key for display
  const formatDataKey = (key: string) => {
    const keyMap: Record<string, string> = {
      total_count: 'Total Scans',
      success_count: 'Successful Scans',
      failed_count: 'Failed Scans',
      success_rate: 'Success Rate',
    };
    return keyMap[key] || key;
  };

  return (
    <div className="flex bg-white rounded-md flex-row items-center w-full">
      <div className="overflow-x-auto">
        <div className="flex flex-col">
          <div className="inline-block min-w-full align-middle">
            <BarChart
              width={1200}
              height={350 + xAxisHeight}
              data={limitedData}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="chartLabel"
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
                {...(sortedData.length > 5 && {
                  interval: 0,
                  angle: -35,
                  textAnchor: "end",
                  height: xAxisHeight,
                })}
              />
              <YAxis
                width={100}
                tickFormatter={formatter}
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
              />
              <Tooltip
                contentStyle={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#4a5568",
                }}
                formatter={formatter}
              />
              <Bar
                dataKey={selectedDataKey}
                fill={barColor}
                name={formatDataKey(selectedDataKey)}
              />
            </BarChart>
          </div>
        </div>
      </div>
    </div>
  );
};

const LineChartIngressEgressComponent = ({
  hourlyData,
}: {
  hourlyData: HourlyData[];
}) => {
  // Handle empty data
  if (!hourlyData || hourlyData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-4">
        <div className="text-gray-500 text-sm">No data to display</div>
      </div>
    );
  }

  // Helper to format hour for display
  const formatHourLabel = (hour: number): string => {
    const period = hour < 12 || hour === 24 ? 'am' : 'pm';
    const formattedHour = (hour % 12) || 12;
    return `${formattedHour}:00${period}`;
  };

  // Create merged chart data starting at hour 5 (5am)
  const mergedChartData = Array.from({ length: 24 }, (_, i) => {
    const dataHour = (5 + i) % 24; // Actual hour index (0-23)
    const displayHour = (5 + i) % 24;
    const nextHour = (displayHour + 1) % 24;

    const dataPoint: any = {
      hour: `${formatHourLabel(displayHour)} - ${formatHourLabel(nextHour)}`
    };

    hourlyData.forEach((dayData, index) => {
      const hourData = dayData.hourlyData[dataHour];
      dataPoint[`ingress_${index}`] = hourData?.ingressCount || 0;
      dataPoint[`egress_${index}`] = hourData?.egressCount || 0;
    });

    return dataPoint;
  });

  const countFormatter = (value: number) => {
    return value.toString();
  };

  // Colors for different days (up to 7)
  const colors = ['#B71C1C', 'black', '#1C1CB7', '#7C1CB7', '#B76A1C', '#1C96B7', '#0B5E0B'];

  return (
    <div className="flex bg-white rounded-md flex-row items-center w-full">
      <div className="overflow-x-auto">
        <div className="flex flex-col">
          <div className="inline-block min-w-full align-middle">
            <LineChart
              width={1180}
              height={400}
              data={mergedChartData}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="hour"
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
                interval={2}
              />
              <YAxis
                width={100}
                tickFormatter={countFormatter}
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
              />
              <Tooltip
                itemSorter={(item) => {
                  return (item.value as number) * -1;
                }}
                contentStyle={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#4a5568",
                }}
                formatter={countFormatter}
              />
              <Legend />
              {hourlyData.flatMap((dayData, index) => [
                <Line
                  key={`ingress-${index}`}
                  type="monotone"
                  dataKey={`ingress_${index}`}
                  name={`${dayData.displayDate} - Ingress`}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={false}
                />,
                <Line
                  key={`egress-${index}`}
                  type="monotone"
                  dataKey={`egress_${index}`}
                  name={`${dayData.displayDate} - Egress`}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              ])}
            </LineChart>
          </div>
        </div>
      </div>
    </div>
  );
};

const LineChartAttendanceComponent = ({
  attendanceData,
}: {
  attendanceData: HourlyAttendanceData[];
}) => {
  // Handle empty data
  if (!attendanceData || attendanceData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-4">
        <div className="text-gray-500 text-sm">No data to display</div>
      </div>
    );
  }

  // Helper to format hour for display
  const formatHourLabel = (hour: number): string => {
    const period = hour < 12 || hour === 24 ? 'am' : 'pm';
    const formattedHour = (hour % 12) || 12;
    return `${formattedHour}:00${period}`;
  };

  // Create merged chart data starting at hour 5 (5am)
  const mergedChartData = Array.from({ length: 24 }, (_, i) => {
    const dataHour = (5 + i) % 24; // Actual hour index (0-23)
    const displayHour = (5 + i) % 24;
    const nextHour = (displayHour + 1) % 24;

    const dataPoint: any = {
      hour: `${formatHourLabel(displayHour)} - ${formatHourLabel(nextHour)}`
    };

    attendanceData.forEach((dayData, index) => {
      const hourData = dayData.hourlyData[dataHour];
      dataPoint[`attendance_${index}`] = hourData?.cumulativeAttendance || 0;
    });

    return dataPoint;
  });

  const countFormatter = (value: number) => {
    return value.toString();
  };

  // Colors for different days (up to 7)
  const colors = ['#B71C1C', 'black', '#1C1CB7', '#7C1CB7', '#B76A1C', '#1C96B7', '#0B5E0B'];

  return (
    <div className="flex bg-white rounded-md flex-row items-center w-full">
      <div className="overflow-x-auto">
        <div className="flex flex-col">
          <div className="inline-block min-w-full align-middle">
            <LineChart
              width={1180}
              height={400}
              data={mergedChartData}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="hour"
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
                interval={2}
              />
              <YAxis
                width={100}
                tickFormatter={countFormatter}
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
              />
              <Tooltip
                itemSorter={(item) => {
                  return (item.value as number) * -1;
                }}
                contentStyle={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#4a5568",
                }}
                formatter={countFormatter}
              />
              <Legend />
              {attendanceData.map((dayData, index) => (
                <Line
                  key={`attendance-${index}`}
                  type="monotone"
                  dataKey={`attendance_${index}`}
                  name={`${dayData.displayDate} - Attendance`}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AccessScansReport = () => {
  const [data, setData] = useState<AccessScansReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayType, setDisplayType] = useState<DisplayType>('by_hour_attendance');
  const [selectedDataKey, setSelectedDataKey] = useState('total_count');
  const [lastFetchedWithFullQuery, setLastFetchedWithFullQuery] = useState(false);
  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const location = useLocation();
  const fetchTimeoutRef = useRef<any>(null);

  // Get unique token types for dynamic columns
  const uniqueTokenTypes = useMemo(() => getUniqueTokenTypes(data), [data]);

  // Get unique locations for dynamic columns
  const uniqueLocations = useMemo(() => getUniqueLocations(data), [data]);

  // Hourly ingress/egress data
  const hourlyIngressEgressData = useMemo(() => {
    if (displayType === 'by_hour_ingress_egress') {
      return formatHourlyIngressEgressData(data);
    }
    return [];
  }, [data, displayType]);

  // Hourly attendance data
  const hourlyAttendanceData = useMemo(() => {
    if (displayType === 'by_hour_attendance') {
      return formatHourlyAttendanceData(data);
    }
    return [];
  }, [data, displayType]);

  // Format data based on display type
  const formattedData = useMemo(() => {
    if (displayType === 'by_hour_ingress_egress') {
      return formatHourlyDataForTable(hourlyIngressEgressData);
    }
    if (displayType === 'by_hour_attendance') {
      return formatHourlyAttendanceDataForTable(hourlyAttendanceData);
    }
    return formatData(data, displayType);
  }, [data, displayType, hourlyIngressEgressData, hourlyAttendanceData]);

  // Get appropriate column definitions
  const currentColDefs = useMemo(() => {
    if (displayType === 'by_hour_ingress_egress' || displayType === 'by_hour_attendance') {
      return generateHourlyColDefs();
    }
    return generateGroupedColDefs(displayType, uniqueTokenTypes, uniqueLocations);
  }, [displayType, uniqueTokenTypes, uniqueLocations]);

  // Conditional auto-size strategy - only for 'all_scans' view
  const autoSizeStrategy: SizeColumnsToContentStrategy | undefined =
    displayType === 'all_scans'
      ? { type: "fitCellContents" }
      : undefined;

  // Chart data - only for grouped views, not 'all_scans'
  const chartRows = useMemo(() => {
    if (displayType === 'all_scans' || displayType === 'by_hour_ingress_egress' || displayType === 'by_hour_attendance') return [];
    return formattedData.filter(row => !row.isTotalRow);
  }, [formattedData, displayType]);

  const handleDataKeyChange = (key: string) => {
    setSelectedDataKey(key);
  };

  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `AccessScansReport_${timestamp}.csv`,
        columnSeparator: ",",
        skipHeader: false,
        allColumns: true,
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const debouncedFetch = () => {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchAccessScans, DEBOUNCE_DELAY);
    };
    const fetchAccessScans = async () => {
      setIsLoading(true);
      try {
        const organizationId = getOrganizationState().organizationId;
        const { eventId } = getEventState();
        const user = getState().user;

        const search = new URLSearchParams(location.search);
        const where = search.get("where");
        // RP-1937: no filter in the address yet; ReportFilters re-applies it and this runs again.
        if (!where) return;
        let whereVariable: any = where ? JSON.parse(where) : {};
        if (organizationId) {
          whereVariable = {
            ...whereVariable,
            organization_id: {
              _eq: organizationId,
            },
          };
        }
        if (user?.vendors) {
          whereVariable = {
            ...whereVariable,
            vendor_id: {
              _in: user?.vendors,
            },
          };
        }

        if (eventId) {
          whereVariable = {
            ...whereVariable,
            event_id: {
              _eq: eventId,
            },
          };
        } else if (user?.events) {
          whereVariable = {
            ...whereVariable,
            event_id: {
              _in: user?.events,
            },
          };
        }

        // Use optimized query that fetches minimal fields for grouping
        // Only use full query for 'all_scans' view which needs all fields
        const queryToUse = displayType === 'all_scans'
          ? ACCESS_SCANS_REPORT
          : ACCESS_SCANS_REPORT_OPTIMIZED;

        const { data: totalData } = await client.query({
          query: ACCESS_SCANS_REPORT_COUNT,
          variables: {
            where: whereVariable,
            limit: 0,
          },
        });
        const totalCount = totalData.total.aggregate.count;
        console.log(`Total records: ${totalCount}, Using ${displayType === 'all_scans' ? 'full' : 'optimized'} query`);

        let offset = 0;
        let chunkData: AccessScansReportRow[] = [];

        // Fetch data in chunks
        while (offset < totalCount) {
          const { data: chunk } = await client.query({
            query: queryToUse,
            variables: {
              where: whereVariable,
              limit: PAGE_SIZE,
              offset: offset,
            },
          });
          chunkData = [...chunkData, ...(chunk.reports_access || [])];
          offset += PAGE_SIZE;
        }

        // Token rename fix: group historical scans under each token's
        // CURRENT name (raw value preserved on access_token_type_raw).
        chunkData = await remapAccessTokenTypes(client, chunkData);

        if (isMounted) {
          setData(chunkData);
          setLastFetchedWithFullQuery(displayType === 'all_scans');
        }
      } catch (error) {
        console.error("Error fetching access scans:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    debouncedFetch();
    return () => {
      isMounted = false;
      clearTimeout(fetchTimeoutRef.current);
    };
  }, [location.search]); // Only refetch when filters change, not when displayType changes

  // Handle switching to 'all_scans' view when we only have optimized data
  useEffect(() => {
    // If user switches to 'all_scans' but we fetched with optimized query, refetch with full query
    if (displayType === 'all_scans' && !lastFetchedWithFullQuery && data.length > 0) {
      const refetchWithFullData = async () => {
        setIsLoading(true);
        try {
          const organizationId = getOrganizationState().organizationId;
          const { eventId } = getEventState();
          const user = getState().user;

          const search = new URLSearchParams(location.search);
          const where = search.get("where");
          let whereVariable: any = where ? JSON.parse(where) : {};
          if (organizationId) {
            whereVariable = {
              ...whereVariable,
              organization_id: {
                _eq: organizationId,
              },
            };
          }
          if (user?.vendors) {
            whereVariable = {
              ...whereVariable,
              vendor_id: {
                _in: user?.vendors,
              },
            };
          }

          if (eventId) {
            whereVariable = {
              ...whereVariable,
              event_id: {
                _eq: eventId,
              },
            };
          } else if (user?.events) {
            whereVariable = {
              ...whereVariable,
              event_id: {
                _in: user?.events,
              },
            };
          }

          const { data: totalData } = await client.query({
            query: ACCESS_SCANS_REPORT_COUNT,
            variables: {
              where: whereVariable,
              limit: 0,
            },
          });
          const totalCount = totalData.total.aggregate.count;

          let offset = 0;
          let chunkData: AccessScansReportRow[] = [];

          while (offset < totalCount) {
            const { data: chunk } = await client.query({
              query: ACCESS_SCANS_REPORT,
              variables: {
                where: whereVariable,
                limit: PAGE_SIZE,
                offset: offset,
              },
            });
            chunkData = [...chunkData, ...(chunk.reports_access || [])];
            offset += PAGE_SIZE;
          }

          chunkData = await remapAccessTokenTypes(client, chunkData);

          setData(chunkData);
          setLastFetchedWithFullQuery(true);
        } catch (error) {
          console.error("Error refetching with full data:", error);
        } finally {
          setIsLoading(false);
        }
      };

      refetchWithFullData();
    }
  }, [displayType]);

  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Access Scans Report" />
        <ReportFilters
          includeSubEvents
          includeDates
          includeTime
          customQueryPaths={{
            dateFrom: "_and[0].adjusted_scan_time",
            dateTo: "_and[1].adjusted_scan_time",
          }}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <div>
            <CustomSelect
              options={displayTypeOptions}
              defaultValue={displayType}
              onChange={(value) => setDisplayType(value as DisplayType)}
              title="Grouping"
            />
          </div>
          <div></div>
          <div className="flex items-end justify-end">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
          </div>
        </div>
      </div>

      {/* Toggle buttons and chart - only show for grouped views */}
      {displayType !== 'all_scans' && !isLoading && chartRows.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap", marginBottom: "4px" }}>
            <ToggleButton
              isSelected={selectedDataKey === "total_count"}
              onClick={() => handleDataKeyChange("total_count")}
            >
              Total Scans
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "success_count"}
              onClick={() => handleDataKeyChange("success_count")}
            >
              Successful
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "failed_count"}
              onClick={() => handleDataKeyChange("failed_count")}
            >
              Failed
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "success_rate"}
              onClick={() => handleDataKeyChange("success_rate")}
            >
              Success Rate
            </ToggleButton>
          </div>
          <BarChartComponent
            rows={chartRows}
            selectedDataKey={selectedDataKey}
            displayType={displayType}
          />
        </>
      )}

      {/* Line chart for hourly ingress/egress */}
      {displayType === 'by_hour_ingress_egress' && !isLoading && hourlyIngressEgressData.length > 0 && (
        <LineChartIngressEgressComponent hourlyData={hourlyIngressEgressData} />
      )}

      {/* Line chart for hourly attendance */}
      {displayType === 'by_hour_attendance' && !isLoading && hourlyAttendanceData.length > 0 && (
        <LineChartAttendanceComponent attendanceData={hourlyAttendanceData} />
      )}

      <div style={{ height: "60vh", marginTop: "4px" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <AgGridReact
              rowData={formattedData}
              columnDefs={currentColDefs}
              rowHeight={rowHeight}
              autoSizeStrategy={autoSizeStrategy}
              pagination={true}
              gridOptions={gridOptions}
              paginationPageSize={100}
              onGridReady={(params) => (gridRef.current = params.api)} // Set GridApi reference
            />
          )}
        </div>
      </div>
    </>
  );
};
